import { useFlags } from "launchdarkly-react-client-sdk";
import { Navigate, useLocation } from "src/router";
import type { RouteObject } from "src/router";
import type { RoutePreload } from "src/router/route-preloads";
import { PageSpinner } from "src/components/loading";
import { useResourcePermission } from "src/components/permission/use-resource-permission";
import { useUser } from "src/contexts/user-context";
import { lazyNamed } from "src/utils/lazy-named";
import { isPermissionsTabAccessible } from "../permissions-tab-access";
import { useFunctionOutletContext } from "./outlet";

const eventFunctionsImport = () => import("./functions");
const FunctionsOverview = lazyNamed(eventFunctionsImport, "FunctionsOverview");

export const preloads: RoutePreload[] = [
  ["/events/functions", eventFunctionsImport],
];
const FunctionLayout = lazyNamed(() => import("./function"), "FunctionLayout");
const CreateFunction = lazyNamed(
  () => import("./function/create"),
  "CreateFunction",
);
const FunctionDetails = lazyNamed(
  () => import("./function/function"),
  "FunctionDetails",
);
const FunctionSyncs = lazyNamed(
  () => import("./function/connected-syncs"),
  "FunctionSyncs",
);
const EventFunctionPermissions = lazyNamed(
  () => import("./function/grants"),
  "EventFunctionPermissions",
);

const EventFunctionPermissionsRoute = () => {
  const location = useLocation();
  const { user } = useUser();
  const { eventsRbacEnabled } = useFlags();
  const { function: fn } = useFunctionOutletContext();
  const permissions = useResourcePermission({
    v2: {
      resource: "event_function",
      grant: "can_update",
      id: fn.id,
    },
  });
  const showPermissions = isPermissionsTabAccessible({
    eventsRbacEnabled,
    resource: fn,
    user,
    permissions,
  });

  if (!eventsRbacEnabled) {
    return (
      <Navigate
        to={{ pathname: "../code", search: location.search }}
        relative="path"
        replace
      />
    );
  }

  if (permissions.isLoading) {
    return <PageSpinner />;
  }

  if (!showPermissions) {
    return (
      <Navigate
        to={{ pathname: "../code", search: location.search }}
        relative="path"
        replace
      />
    );
  }

  return <EventFunctionPermissions />;
};

export const routes: Array<RouteObject> = [
  {
    index: true,
    spacesAccess: "hidden-from-limited-view",
    element: <FunctionsOverview />,
  },
  {
    path: "new",
    spacesAccess: "hidden-from-limited-view",
    element: <CreateFunction />,
  },
  {
    path: ":id",
    spacesAccess: "accessible-from-limited-view",
    element: <FunctionLayout />,
    children: [
      {
        path: "code",
        spacesAccess: "hidden-from-limited-view",
        element: <FunctionDetails />,
      },
      {
        path: "syncs",
        spacesAccess: "hidden-from-limited-view",
        element: <FunctionSyncs />,
      },
      {
        path: "permissions",
        spacesAccess: "hidden-from-limited-view",
        element: <EventFunctionPermissionsRoute />,
      },
      {
        index: true,
        spacesAccess: "hidden-from-limited-view",
        element: (
          <Navigate
            to={{ pathname: "code", search: location.search }}
            replace
          />
        ),
      },
    ],
  },
];
